import { parseDateNumberToNumber } from '../../core/components/annotations/utils/parse-date-number-to-number.js';
import type { HistogramSeries } from '../../histogram/types/histogram.js';
import type {
  XYChartAreaSeriesActionItem,
  XYChartBarSeriesActionItem,
  XYChartDotSeriesActionItem,
  XYChartLineSeriesActionItem,
  XYChartRectSeriesActionItem,
  XYChartSelectedDomains,
  XYChartSelectedSeries,
} from '../../xy-chart/types/xy-chart.js';

type XYSelectableValue = number | string | Date | undefined;

const toNumber = (value: XYSelectableValue): number =>
  typeof value === 'string' ? Number(value) : parseDateNumberToNumber(value);

type XYSelectedDatapoint =
  | XYChartRectSeriesActionItem
  | XYChartDotSeriesActionItem
  | XYChartLineSeriesActionItem
  | XYChartBarSeriesActionItem
  | XYChartAreaSeriesActionItem;

const mapDatapointToHistogramBin = (datapoint: XYSelectedDatapoint) => {
  // Handle both bar (nested in .datapoint) and other types (direct properties)
  const barData = 'datapoint' in datapoint ? datapoint.datapoint : datapoint;
  const { x0, x1, y1 } = barData as {
    x0: XYSelectableValue;
    x1: XYSelectableValue;
    y1: XYSelectableValue;
  };

  return {
    from: x0,
    to: x1,
    value: y1,
  };
};

export const mapSelectedSeriesToHistogram = (
  selectedSeries: XYChartSelectedSeries,
): HistogramSeries[] => {
  return selectedSeries.flatMap((series) =>
    Object.entries(series)
      .map(([name, datapoints]) => {
        const bins = datapoints.map((datapoint) =>
          mapDatapointToHistogramBin(datapoint),
        );

        if (!bins.length) {
          return null;
        }

        return {
          name,
          bins,
        };
      })
      .filter((series): series is HistogramSeries => series !== null),
  );
};

export const mapSelectionDomainToHistogramDomains = (
  selectionDomain: XYChartSelectedDomains,
): [number, number] => {
  const [[, firstDomain]] = Object.entries(selectionDomain[0]);
  return [toNumber(firstDomain[0]), toNumber(firstDomain[1])];
};
